import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import ConfidenceSlider from '../../match-predictions/components/ConfidenceSlider';
import PredictionSection from '../../match-predictions/components/PredictionSection';
import { predictionsService } from '../../../services/supabaseService';
import Toast from '../../../components/ui/Toast';


const PredictionsTab = ({ tournament, onSubmitPredictions }) => {
  const [predictions, setPredictions] = useState({
    champion: '',
    semifinalists: [],
    finalists: [],
    playerOfTournament: '',
    championConfidence: 2,
    semifinalistsConfidence: 2,
    finalistsConfidence: 2,
    playerConfidence: 2,
    top4Teams: [],
    top4Confidence: 2,
    superOverTeam: '',
    superOverConfidence: 2
  });

  const [savedPredictions, setSavedPredictions] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('');
  const [timeRemaining, setTimeRemaining] = useState('');
  const [loadingPredictions, setLoadingPredictions] = useState(true);
  const [isLocked, setIsLocked] = useState(false);
  const [lockTime, setLockTime] = useState(null);
  const [extendedLockTime, setExtendedLockTime] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [errorModal, setErrorModal] = useState(null);
  const [openSections, setOpenSections] = useState({
    champion: true,
    semifinalists: false,
    finalists: false,
    player: false
  });

  // Derive enabled settings from tournament prediction settings
  const predSettings = tournament?.predictionSettings?.tournamentLevel || {};
  const championEnabled = predSettings?.championPrediction?.enabled !== false;
  const semifinalistsEnabled = predSettings?.semifinalistTeams?.enabled !== false;
  const finalistsEnabled = predSettings?.finalistTeams?.enabled !== false;
  const playerEnabled = predSettings?.playerOfTournament?.enabled !== false;
  const top4Enabled = predSettings?.top4Teams?.enabled === true;
  const superOverEnabled = predSettings?.superOverTeam?.enabled === true;
  const top4Points = predSettings?.top4Teams?.points || 10;
  const top4OrderPoints = predSettings?.top4Teams?.orderPoints || 5;
  const superOverPoints = predSettings?.superOverTeam?.points || 15;

  // Load saved predictions and lock status on mount
  useEffect(() => {
    const loadSavedPredictions = async () => {
      if (!tournament?.id) return;
      
      try {
        setLoadingPredictions(true);
        const [savedPrediction, lockStatus] = await Promise.all([
          predictionsService?.getTournamentPrediction(tournament?.id),
          predictionsService?.checkTournamentPredictionLock(tournament?.id)
        ]);
        
        if (savedPrediction) {
          const loadedPredictions = {
            champion: savedPrediction?.championTeamId || '',
            semifinalists: [
              savedPrediction?.semifinalist1Id,
              savedPrediction?.semifinalist2Id,
              savedPrediction?.semifinalist3Id,
              savedPrediction?.semifinalist4Id
            ]?.filter(Boolean),
            finalists: [
              savedPrediction?.finalist1Id,
              savedPrediction?.finalist2Id
            ]?.filter(Boolean),
            playerOfTournament: savedPrediction?.playerOfTournamentId || '',
            championConfidence: savedPrediction?.championConfidence || 2,
            semifinalistsConfidence: savedPrediction?.semifinalistsConfidence || 2,
            finalistsConfidence: savedPrediction?.finalistsConfidence || 2,
            playerConfidence: savedPrediction?.playerConfidence || 2,
            top4Teams: [
              savedPrediction?.top4Team1Id,
              savedPrediction?.top4Team2Id,
              savedPrediction?.top4Team3Id,
              savedPrediction?.top4Team4Id
            ]?.filter(Boolean),
            top4Confidence: savedPrediction?.top4Confidence || 2,
            superOverTeam: savedPrediction?.superOverTeamId || '',
            superOverConfidence: savedPrediction?.superOverConfidence || 2
          };
          setPredictions(loadedPredictions);
          setSavedPredictions(loadedPredictions);
        }

        // Set lock status - use effectivelyLocked which considers both time lock and override setting
        setIsLocked(lockStatus?.effectivelyLocked || false);
        setLockTime(lockStatus?.lockTime);
        setExtendedLockTime(lockStatus?.extendedLockTime);
      } catch (error) {
        console.error('Error loading saved predictions:', error);
      } finally {
        setLoadingPredictions(false);
      }
    };

    loadSavedPredictions();
  }, [tournament?.id]);

  useEffect(() => {
    const calculateTimeRemaining = () => {
      if (!lockTime) {
        setTimeRemaining('Lock time not set');
        return;
      }

      const now = new Date();
      const effectiveLock = extendedLockTime || lockTime;
      const lock = new Date(effectiveLock);
      const diff = lock - now;

      if (diff <= 0) {
        setTimeRemaining('Predictions Locked');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (days > 0) {
        setTimeRemaining(`${days}d ${hours}h remaining`);
      } else if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m remaining`);
      } else {
        setTimeRemaining(`${minutes}m remaining`);
      }
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 60000);

    return () => clearInterval(interval);
  }, [lockTime, extendedLockTime]);

  useEffect(() => {
    const autoSaveTimer = setTimeout(() => {
      if (predictions?.champion || predictions?.semifinalists?.length > 0) {
        setAutoSaveStatus('Auto-saved');
        setTimeout(() => setAutoSaveStatus(''), 2000);
      }
    }, 2000);

    return () => clearTimeout(autoSaveTimer);
  }, [predictions]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1024px)');

    const handleMediaChange = (e) => {
      if (e?.matches) {
        setOpenSections({
          champion: true,
          semifinalists: true,
          finalists: true,
          player: true
        });
      } else {
        setOpenSections({
          champion: true,
          semifinalists: false,
          finalists: false,
          player: false
        });
      }
    };

    handleMediaChange(mediaQuery);
    mediaQuery?.addEventListener('change', handleMediaChange);

    return () => mediaQuery?.removeEventListener('change', handleMediaChange);
  }, []);

  const handleChampionChange = (value) => {
    setPredictions({ ...predictions, champion: value });
    if (value) setValidationErrors(prev => ({ ...prev, champion: '' }));
  };

  const handleSemifinalistsChange = (values) => {
    if (values?.length <= 4) {
      setPredictions({ ...predictions, semifinalists: values });
      if (values?.length === 4) setValidationErrors(prev => ({ ...prev, semifinalists: '' }));
    }
  };

  const handleFinalistsChange = (values) => {
    if (values?.length <= 2) {
      setPredictions({ ...predictions, finalists: values });
      if (values?.length === 2) setValidationErrors(prev => ({ ...prev, finalists: '' }));
    }
  };

  const handlePlayerChange = (value) => {
    setPredictions({ ...predictions, playerOfTournament: value });
    if (value) setValidationErrors(prev => ({ ...prev, playerOfTournament: '' }));
  };

  const handleConfidenceChange = (field, value) => {
    setPredictions({ ...predictions, [field]: value });
  };

  const handleTop4Change = (values) => {
    if (values?.length <= 4) {
      setPredictions({ ...predictions, top4Teams: values });
      if (values?.length === 4) setValidationErrors(prev => ({ ...prev, top4Teams: '' }));
    }
  };

  const handleSuperOverTeamChange = (value) => {
    setPredictions({ ...predictions, superOverTeam: value });
    if (value) setValidationErrors(prev => ({ ...prev, superOverTeam: '' }));
  };

  const handleSubmit = () => {
    if (isLocked) {
      setErrorModal({
        title: 'Predictions Deadline Passed',
        message: 'The tournament prediction deadline has passed. You can no longer submit or modify your predictions.'
      });
      return;
    }

    const errors = {};
    if (championEnabled && !predictions?.champion) errors.champion = 'Please select a Tournament Champion.';
    if (semifinalistsEnabled && predictions?.semifinalists?.length !== 4) errors.semifinalists = `Please select exactly 4 Semifinalist Teams (${predictions?.semifinalists?.length}/4 selected).`;
    if (finalistsEnabled && predictions?.finalists?.length !== 2) errors.finalists = `Please select exactly 2 Finalist Teams (${predictions?.finalists?.length}/2 selected).`;
    if (playerEnabled && !predictions?.playerOfTournament) errors.playerOfTournament = 'Please select a Player of the Tournament.';
    if (top4Enabled && predictions?.top4Teams?.length !== 4) errors.top4Teams = `Please select exactly 4 Top 4 Teams (${predictions?.top4Teams?.length}/4 selected).`;
    if (superOverEnabled && !predictions?.superOverTeam) errors.superOverTeam = 'Please select a Super Over Team.';

    if (Object.keys(errors)?.length > 0) {
      setValidationErrors(errors);
      const firstErrorKey = Object.keys(errors)?.[0];
      const el = document.querySelector(`[data-prediction-section="${firstErrorKey}"]`);
      if (el) el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setValidationErrors({});
    onSubmitPredictions(predictions, setIsSubmitting, setToast, setErrorModal);
  };

  const handleReset = () => {
    if (savedPredictions) {
      setPredictions({ ...savedPredictions });
    } else {
      setPredictions({
        champion: '',
        semifinalists: [],
        finalists: [],
        playerOfTournament: '',
        championConfidence: 2,
        semifinalistsConfidence: 2,
        finalistsConfidence: 2,
        playerConfidence: 2,
        top4Teams: [],
        top4Confidence: 2,
        superOverTeam: '',
        superOverConfidence: 2
      });
    }
  };

  const toggleSection = (section) => {
    if (window.innerWidth < 1024) {
      setOpenSections((prev) => ({
        ...prev,
        [section]: !prev?.[section]
      }));
    }
  };

  const calculatePoints = () => {
    let total = 0;
    const confidenceLevels = [1.0, 1.5, 2.0, 2.5];

    const championPoints = tournament?.predictionSettings?.tournamentLevel?.championPrediction?.points || 50;
    const semifinalistsPointsPerTeam = (tournament?.predictionSettings?.tournamentLevel?.semifinalistTeams?.points || 10);
    const semifinalistsPoints = semifinalistsPointsPerTeam * 4;
    const finalistsPoints = tournament?.predictionSettings?.tournamentLevel?.finalistTeams?.points || 25;
    const playerPoints = tournament?.predictionSettings?.tournamentLevel?.playerOfTournament?.points || 30;
    const top4Pts = predSettings?.top4Teams?.points || 10;
    const top4OrderPts = predSettings?.top4Teams?.orderPoints || 5;
    const superOverPts = predSettings?.superOverTeam?.points || 15;

    const getMultiplier = (confidenceLevel) => {
      if (!tournament?.enableConfidenceSlider) return 1.0;
      return confidenceLevels?.[confidenceLevel - 1] || 1.0;
    };

    if (championEnabled && predictions?.champion) total += Math.round(championPoints * getMultiplier(predictions?.championConfidence));
    if (semifinalistsEnabled && predictions?.semifinalists?.length === 4) total += Math.round(semifinalistsPoints * getMultiplier(predictions?.semifinalistsConfidence));
    if (finalistsEnabled && predictions?.finalists?.length === 2) total += Math.round(finalistsPoints * getMultiplier(predictions?.finalistsConfidence));
    if (playerEnabled && predictions?.playerOfTournament) total += Math.round(playerPoints * getMultiplier(predictions?.playerConfidence));
    if (top4Enabled && predictions?.top4Teams?.length === 4) total += Math.round((top4Pts + top4OrderPts) * 4 * getMultiplier(predictions?.top4Confidence));
    if (superOverEnabled && predictions?.superOverTeam) total += Math.round(superOverPts * getMultiplier(predictions?.superOverConfidence));

    return total;
  };

  const teamOptions = tournament?.teams?.map(team => ({
    value: team?.id,
    label: team?.shortName ? `${team?.shortName} - ${team?.name}` : team?.name
  }));

  const playerOptions = tournament?.players?.map(player => ({
    value: player?.id,
    label: `${player?.name} (${player?.team})`
  }));

  // Get base points from prediction settings or use defaults
  const championPoints = tournament?.predictionSettings?.tournamentLevel?.championPrediction?.points || 50;
  const semifinalistsPointsPerTeam = (tournament?.predictionSettings?.tournamentLevel?.semifinalistTeams?.points || 10);
  const semifinalistsPoints = semifinalistsPointsPerTeam * 4;
  const finalistsPoints = tournament?.predictionSettings?.tournamentLevel?.finalistTeams?.points || 25;
  const playerPoints = tournament?.predictionSettings?.tournamentLevel?.playerOfTournament?.points || 30;

  const getConfidenceMultiplier = (confidenceLevel) => {
    if (!tournament?.enableConfidenceSlider) return 1.0;
    return [1.0, 1.5, 2.0, 2.5]?.[confidenceLevel - 1] || 1.0;
  };

  const summaryData = [
    ...(championEnabled ? [{
      label: 'Tournament Champion',
      value: predictions?.champion ? tournament?.teams?.find(t => t?.id === predictions?.champion)?.name : 'Not selected',
      points: predictions?.champion ? Math.round(championPoints * getConfidenceMultiplier(predictions?.championConfidence)) : 0,
      icon: 'Trophy'
    }] : []),
    ...(semifinalistsEnabled ? [{
      label: 'Semifinalist Teams',
      value: predictions?.semifinalists?.length === 4 
        ? `${predictions?.semifinalists?.length} teams selected` 
        : `${predictions?.semifinalists?.length}/4 teams selected`,
      points: predictions?.semifinalists?.length === 4 ? Math.round(semifinalistsPoints * getConfidenceMultiplier(predictions?.semifinalistsConfidence)) : 0,
      icon: 'Users'
    }] : []),
    ...(finalistsEnabled ? [{
      label: 'Finalist Teams',
      value: predictions?.finalists?.length === 2 
        ? `${predictions?.finalists?.length} teams selected` 
        : `${predictions?.finalists?.length}/2 teams selected`,
      points: predictions?.finalists?.length === 2 ? Math.round(finalistsPoints * getConfidenceMultiplier(predictions?.finalistsConfidence)) : 0,
      icon: 'Award'
    }] : []),
    ...(playerEnabled ? [{
      label: 'Player of the Tournament',
      value: predictions?.playerOfTournament ? tournament?.players?.find(p => p?.id === predictions?.playerOfTournament)?.name : 'Not selected',
      points: predictions?.playerOfTournament ? Math.round(playerPoints * getConfidenceMultiplier(predictions?.playerConfidence)) : 0,
      icon: 'Star'
    }] : []),
  ];

  return (
    <div className="space-y-6">
      {/* Toast notification */}
      {toast && (
        <Toast
          message={toast?.message}
          type={toast?.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Error Modal */}
      {errorModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-card rounded-lg shadow-elevation-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="AlertCircle" size={24} className="text-destructive" />
              </div>
              <h3 className="text-xl font-bold text-foreground">{errorModal?.title || 'Submission Failed'}</h3>
            </div>
            <p className="text-sm md:text-base text-muted-foreground mb-6">
              {errorModal?.message || 'An error occurred. Please try again.'}
            </p>
            <Button
              variant="default"
              fullWidth
              onClick={() => setErrorModal(null)}
            >
              Close
            </Button>
          </div>
        </div>
      )}

      {/* Lock Status Banner */}
      {isLocked && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-center gap-3">
          <Icon name="Lock" size={20} className="text-destructive" />
          <div>
            <p className="font-semibold text-destructive">Predictions Locked</p>
            <p className="text-sm text-muted-foreground">The deadline has passed. You can no longer modify your predictions.</p>
          </div>
        </div>
      )}
      {/* Time Remaining Banner */}
      {!isLocked && lockTime && (
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon name="Clock" size={20} className="text-primary" />
            <div>
              <p className="font-semibold text-foreground">Time Remaining</p>
              <p className="text-sm text-muted-foreground">{timeRemaining}</p>
            </div>
          </div>
          {autoSaveStatus && (
            <span className="text-sm text-green-600 font-medium">{autoSaveStatus}</span>
          )}
        </div>
      )}
      {loadingPredictions ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : (
        <>
          {/* Predictions Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 overflow-visible" style={{marginBottom: "100px"}}>
            <div className="space-y-4 md:space-y-6 overflow-visible">
              {championEnabled && (
              <div data-prediction-section="champion">
              <PredictionSection
                title="Tournament Champion"
                icon="Trophy"
                points={championPoints}
                isOpen={openSections?.champion}
                onToggle={() => toggleSection('champion')}
                sectionId="champion"
                hasError={!!validationErrors?.champion}
                required
              >
                <Select
                  label="Select Champion"
                  description="Choose the team you predict will win the tournament"
                  options={teamOptions}
                  value={predictions?.champion}
                  onChange={handleChampionChange}
                  disabled={isLocked}
                  searchable
                  required
                  error={validationErrors?.champion}
                  className="mb-4"
                />
                {tournament?.enableConfidenceSlider && (
                  <ConfidenceSlider
                    value={predictions?.championConfidence}
                    onChange={(value) => handleConfidenceChange('championConfidence', value)}
                    disabled={isLocked}
                    basePoints={championPoints}
                  />
                )}
              </PredictionSection>
              </div>
              )}

              {semifinalistsEnabled && (
              <div data-prediction-section="semifinalists">
              <PredictionSection
                title="Semifinalist Teams"
                icon="Users"
                points={semifinalistsPoints}
                isOpen={openSections?.semifinalists}
                onToggle={() => toggleSection('semifinalists')}
                sectionId="semifinalists"
                hasError={!!validationErrors?.semifinalists}
                required
              >
                <Select
                  label="Select 4 Semifinalist Teams"
                  description="Choose the four teams you predict will reach the semifinals"
                  options={teamOptions}
                  value={predictions?.semifinalists}
                  onChange={handleSemifinalistsChange}
                  disabled={isLocked}
                  multiple
                  searchable
                  required
                  error={validationErrors?.semifinalists}
                  className="mb-4"
                />
                {predictions?.semifinalists?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {predictions?.semifinalists?.map((teamId) => {
                      const team = tournament?.teams?.find(t => t?.id === teamId);
                      return (
                        <span key={teamId} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                          {team?.name || teamId}
                        </span>
                      );
                    })}
                  </div>
                )}
                {tournament?.enableConfidenceSlider && (
                  <ConfidenceSlider
                    value={predictions?.semifinalistsConfidence}
                    onChange={(value) => handleConfidenceChange('semifinalistsConfidence', value)}
                    disabled={isLocked}
                    basePoints={semifinalistsPoints}
                  />
                )}
              </PredictionSection>
              </div>
              )}

              {finalistsEnabled && (
              <div data-prediction-section="finalists">
              <PredictionSection
                title="Finalist Teams"
                icon="Award"
                points={finalistsPoints}
                isOpen={openSections?.finalists}
                onToggle={() => toggleSection('finalists')}
                sectionId="finalists"
                hasError={!!validationErrors?.finalists}
                required
              >
                <Select
                  label="Select 2 Finalist Teams"
                  description="Choose the two teams you predict will reach the final"
                  options={teamOptions}
                  value={predictions?.finalists}
                  onChange={handleFinalistsChange}
                  disabled={isLocked}
                  multiple
                  searchable
                  required
                  error={validationErrors?.finalists}
                  className="mb-4"
                />
                {predictions?.finalists?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {predictions?.finalists?.map((teamId) => {
                      const team = tournament?.teams?.find(t => t?.id === teamId);
                      return (
                        <span key={teamId} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-rose-500/10 text-rose-700 dark:text-rose-300 border border-rose-500/20">
                          {team?.name || teamId}
                        </span>
                      );
                    })}
                  </div>
                )}
                {tournament?.enableConfidenceSlider && (
                  <ConfidenceSlider
                    value={predictions?.finalistsConfidence}
                    onChange={(value) => handleConfidenceChange('finalistsConfidence', value)}
                    disabled={isLocked}
                    basePoints={finalistsPoints}
                  />
                )}
              </PredictionSection>
              </div>
              )}

              {playerEnabled && (
              <div data-prediction-section="playerOfTournament">
              <PredictionSection
                title="Player of the Tournament"
                icon="Star"
                points={playerPoints}
                isOpen={openSections?.player}
                onToggle={() => toggleSection('player')}
                sectionId="playerOfTournament"
                hasError={!!validationErrors?.playerOfTournament}
                required
              >
                <Select
                  label="Select Player"
                  description="Choose the player you predict will be named Player of the Tournament"
                  options={playerOptions}
                  value={predictions?.playerOfTournament}
                  onChange={handlePlayerChange}
                  disabled={isLocked}
                  searchable
                  required
                  error={validationErrors?.playerOfTournament}
                  className="mb-4"
                />
                {tournament?.enableConfidenceSlider && (
                  <ConfidenceSlider
                    value={predictions?.playerConfidence}
                    onChange={(value) => handleConfidenceChange('playerConfidence', value)}
                    disabled={isLocked}
                    basePoints={playerPoints}
                  />
                )}
              </PredictionSection>
              </div>
              )}

              {/* Top 4 Teams Prediction Card - shown only if enabled */}
              {top4Enabled && (
              <div data-prediction-section="top4Teams">
              <PredictionSection
                title="Top 4 Teams"
                icon="ListOrdered"
                points={(top4Points + top4OrderPoints) * 4}
                isOpen={openSections?.top4Teams}
                onToggle={() => toggleSection('top4Teams')}
                sectionId="top4Teams"
                hasError={!!validationErrors?.top4Teams}
                required
              >
                <Select
                  label="Select Top 4 Teams (in order)"
                  description="Select 4 teams in the order you predict they will finish (1st to 4th)"
                  options={teamOptions}
                  value={predictions?.top4Teams}
                  onChange={handleTop4Change}
                  disabled={isLocked || !top4Enabled}
                  multiple
                  searchable
                  required
                  error={validationErrors?.top4Teams}
                  className="mb-2"
                />
                {predictions?.top4Teams?.length > 0 && predictions?.top4Teams?.length <= 4 && (
                  <div className="flex flex-wrap gap-2">
                    {predictions?.top4Teams?.map((teamId, idx) => {
                      const team = tournament?.teams?.find(t => t?.id === teamId);
                      return (
                        <span key={teamId} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/20">
                          <span className="w-4 h-4 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">{idx + 1}</span>
                          {team?.name || teamId}
                        </span>
                      );
                    })}
                  </div>
                )}
                {tournament?.enableConfidenceSlider && (
                  <ConfidenceSlider
                    value={predictions?.top4Confidence}
                    onChange={(value) => handleConfidenceChange('top4Confidence', value)}
                    disabled={isLocked || !top4Enabled}
                    basePoints={(top4Points + top4OrderPoints) * 4}
                  />
                )}
              </PredictionSection>
              </div>
              )}

              {/* Super Over Team Prediction Card - shown only if enabled */}
              {superOverEnabled && (
              <div data-prediction-section="superOverTeam">
              <PredictionSection
                title="Super Over Team"
                icon="Zap"
                points={superOverPoints}
                isOpen={openSections?.superOverTeam}
                onToggle={() => toggleSection('superOverTeam')}
                sectionId="superOverTeam"
                hasError={!!validationErrors?.superOverTeam}
                required
              >
                <Select
                  label="Select Team"
                  description="Choose the team you predict will be involved in a Super Over match"
                  options={teamOptions}
                  value={predictions?.superOverTeam}
                  onChange={handleSuperOverTeamChange}
                  disabled={isLocked || !superOverEnabled}
                  searchable
                  required
                  error={validationErrors?.superOverTeam}
                  className="mb-2"
                />
                {tournament?.enableConfidenceSlider && (
                  <ConfidenceSlider
                    value={predictions?.superOverConfidence}
                    onChange={(value) => handleConfidenceChange('superOverConfidence', value)}
                    disabled={isLocked || !superOverEnabled}
                    basePoints={superOverPoints}
                  />
                )}
              </PredictionSection>
              </div>
              )}
            </div>

            <div className="lg:col-span-1">
              <div className="bg-card rounded-lg shadow-elevation-md p-4 md:p-6 lg:p-8 sticky top-20 lg:top-24">
                <div className="flex items-center gap-3 mb-4 md:mb-6">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon name="FileText" size={20} className="text-primary" />
                  </div>
                  <h3 className="text-lg md:text-xl font-semibold text-foreground">Prediction Summary</h3>
                </div>
                <div className="space-y-3 md:space-y-4 mb-4 md:mb-6">
                  {summaryData?.map((item, index) => (
                    <div key={index} className="flex items-start justify-between gap-3 pb-3 border-b border-border last:border-0">
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        <Icon name={item?.icon} size={16} className="text-primary mt-1 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs md:text-sm font-medium text-foreground">{item?.label}</p>
                          <p className="text-xs md:text-sm text-muted-foreground truncate">{item?.value}</p>
                        </div>
                      </div>
                      <span className="text-sm md:text-base font-semibold text-primary whitespace-nowrap">
                        {item?.points} pts
                      </span>
                    </div>
                  ))}
                  {top4Enabled && (
                    <div className="flex items-start justify-between gap-3 pb-3 border-b border-border last:border-0">
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        <Icon name="ListOrdered" size={16} className="text-amber-500 mt-1 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs md:text-sm font-medium text-foreground">Top 4 Teams</p>
                          <p className="text-xs md:text-sm text-muted-foreground truncate">
                            {predictions?.top4Teams?.length === 4 ? '4 teams selected' : `${predictions?.top4Teams?.length}/4 teams selected`}
                          </p>
                        </div>
                      </div>
                      <span className="text-sm md:text-base font-semibold text-amber-600 dark:text-amber-400 whitespace-nowrap">
                        {predictions?.top4Teams?.length === 4 ? `${Math.round((top4Points + top4OrderPoints) * 4 * getConfidenceMultiplier(predictions?.top4Confidence))} pts` : '0 pts'}
                      </span>
                    </div>
                  )}
                  {superOverEnabled && (
                    <div className="flex items-start justify-between gap-3 pb-3 border-b border-border last:border-0">
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        <Icon name="Zap" size={16} className="text-purple-500 mt-1 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs md:text-sm font-medium text-foreground">Super Over Team</p>
                          <p className="text-xs md:text-sm text-muted-foreground truncate">
                            {predictions?.superOverTeam ? tournament?.teams?.find(t => t?.id === predictions?.superOverTeam)?.name || 'Selected' : 'Not selected'}
                          </p>
                        </div>
                      </div>
                      <span className="text-sm md:text-base font-semibold text-purple-600 dark:text-purple-400 whitespace-nowrap">
                        {predictions?.superOverTeam ? `${Math.round(superOverPoints * getConfidenceMultiplier(predictions?.superOverConfidence))} pts` : '0 pts'}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-3 p-4 bg-muted/50 rounded-lg mb-4 md:mb-6">
                  <div className="flex items-center justify-between">
                    <span className="text-sm md:text-base text-muted-foreground">Total Potential:</span>
                    <span className="text-xl md:text-2xl font-bold text-primary">{calculatePoints()} pts</span>
                  </div>
                </div>
                <div className="space-y-2 md:space-y-3">
                  <Button
                    variant="default"
                    fullWidth
                    onClick={handleSubmit}
                    disabled={isLocked || isSubmitting}
                    loading={isSubmitting}
                    iconName={isSubmitting ? undefined : "Send"}
                    iconPosition="right"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Predictions'}
                  </Button>
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={handleReset}
                    disabled={isLocked || isSubmitting}
                    iconName="RotateCcw"
                    iconPosition="left"
                  >
                    Reset Predictions
                  </Button>
                </div>
                <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t border-border">
                  <div className="flex items-start gap-2">
                    <Icon name="Info" size={16} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-muted-foreground">
                      Predictions can be modified until the tournament lock time. Points are calculated automatically after tournament results are posted.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PredictionsTab;